import React from 'react';
import cn from 'classnames';

import { BaseModal } from 'components/ui';
import { BaseButton } from 'components/ui/buttons';
import { Options, usePDF } from 'react-to-pdf';
import { useQrCodeSvg } from 'hooks/useQrCodeSvg';
import { Template1_3x1, Template24_3x8_1x2, Template2_4x6, TemplateComponentProps } from './Templates';

type QRTemplatesModalProps = {
  onCancelClick: () => void;
  refId: number;
  patientId: string;
};

type TemplateConfig = {
  id: string;
  name: string;
  counts: number;
  size: number;
  blockClass: string;
  gridClass: string;
  TemplateComponent: React.ComponentType<TemplateComponentProps>;
  pdfOptions: Options;
};

const TEMPLATES: TemplateConfig[] = [
  {
    id: '1',
    name: '24-1x2',
    counts: 24,
    size: 80,
    blockClass: 'w-[20px] h-[10px]',
    gridClass: 'px-[5px] grid-rows-8 grid-cols-3 align items-center justify-center gap-x-2 gap-y-[2px]',
    TemplateComponent: Template24_3x8_1x2,
    pdfOptions: {
      page: { format: 'LETTER', margin: 0, orientation: 'portrait' },
    },
  },
  {
    id: '2',
    name: '2-4x6',
    size: 360,
    counts: 2,
    blockClass: 'w-[72px] h-[48px]',
    gridClass: 'px-[5px]  grid-rows-2 grid-cols-1 gap-[2px]',
    TemplateComponent: Template2_4x6,
    pdfOptions: {
      page: { format: 'LETTER', margin: 0, orientation: 'portrait' },
    },
  },
  {
    id: '3',
    name: '1-3x1',
    size: 56,
    counts: 1,
    blockClass: 'w-[76px] h-[24px]',
    gridClass: 'px-[5px] grid-rows-1 grid-cols-1 justify-center',
    TemplateComponent: Template1_3x1,
    pdfOptions: {
      page: { format: [76.2, 25.4], margin: 0, orientation: 'landscape' },
      canvas: { mimeType: 'image/png' },
      overrides: {
        pdf: { format: [76.2, 25.4] },
      },
    },
  },
];

export const QRTemplatesModal: React.FC<QRTemplatesModalProps> = ({ patientId, onCancelClick, refId }) => {
  const [selectedTemplate, setSelectedTemplate] = React.useState<string | null>(null);
  const [isTemplateReady, setIsTemplateReady] = React.useState(false);
  const { toPDF, targetRef } = usePDF();

  const template = TEMPLATES.find(template => template.id === selectedTemplate);
  const TemplateComponent = template?.TemplateComponent;
  const size = template?.size ?? 160;
  const { qrCodeSvg, loading } = useQrCodeSvg(patientId, size);

  React.useEffect(() => {
    setIsTemplateReady(false);
  }, [qrCodeSvg, selectedTemplate]);

  return (
    <>
      {TemplateComponent && qrCodeSvg && (
        <div className="fixed bottom-full right-full z-100" ref={targetRef}>
          <TemplateComponent
            qrCodeSvg={qrCodeSvg}
            size={size}
            refId={refId}
            onRenderReady={ready => {
              setIsTemplateReady(ready);
            }}
          />
        </div>
      )}
      <BaseModal isShownCloseButton onCloseClick={onCancelClick}>
        <div className="flex flex-col mt-4 items-center w-[460px] text-center">
          <h2 className="mb-4 text-gray-900 dark:text-white-400 text-[28px] font-semibold leading-[normal]">
            Print QR code for patient ID:&nbsp;{refId}
          </h2>
          <p className="mb-4 text-sky dark:text-silver-400 text-center text-sm font-semibold leading-[140%]">
            Choose Print Layout:
          </p>

          <div className="grid grid-rows-2 grid-cols-4 gap-4">
            {TEMPLATES.map(template => (
              <button
                type="button"
                key={template.id}
                className={cn(
                  'flex flex-col max-h-[140px] items-center gap-1 border p-2 rounded-lg border-solid hover:bg-white-300',
                  selectedTemplate === template.id
                    ? 'border-sea-serpent bg-mint dark:bg-mint/20'
                    : 'bg-white-400 dark:bg-transparent border-gray-200 dark:border-sky',
                )}
                onClick={() => setSelectedTemplate(template.id)}
              >
                <p className="text-silver-800 dark:text-white-400 text-xs font-semibold leading-[17px]">
                  {template.name}
                </p>
                <span className={cn('w-full grid', template.gridClass)}>
                  {Array(template.counts)
                    .fill(null)
                    .map((_, index) => (
                      <span
                        key={index}
                        className={cn(
                          selectedTemplate === template.id ? 'bg-teal-300' : 'bg-iron dark:bg-sky',
                          template.blockClass,
                        )}
                      />
                    ))}
                </span>
              </button>
            ))}
            {/* <button
              type="button"
              className={cn(
                'flex flex-col max-h-[140px] items-center gap-1 border p-2 rounded-lg border-solid hover:bg-white-300',
                selectedTemplate === 'wristband'
                  ? 'border-sea-serpent bg-mint dark:bg-mint/20'
                  : 'bg-white-400 dark:bg-transparent border-gray-200 dark:border-sky',
              )}
              onClick={() => setSelectedTemplate('wristband')}
            >
              <p className="text-silver-800 dark:text-white-400 text-xs font-semibold leading-[17px]">
                Hospital Wristband
              </p>
              <span
                className={cn('w-full grid ', selectedTemplate === 'wristband' ? 'text-teal-300' : 'text-gray-800')}
              >
                <WristbandIcon />
              </span>
            </button> */}
          </div>

          <div className="mt-8 w-full flex items-center justify-end gap-4">
            <BaseButton scheme="secondary" onClick={onCancelClick}>
              Cancel
            </BaseButton>
            <BaseButton
              scheme="primary"
              size="md"
              onClick={() => {
                if (!template) return;

                toPDF({
                  filename: `QR_${refId}.pdf`,
                  method: 'save',
                  ...template.pdfOptions,
                });
              }}
              disabled={!selectedTemplate || loading || !qrCodeSvg || !isTemplateReady}
            >
              Print
            </BaseButton>
          </div>
        </div>
      </BaseModal>
    </>
  );
};
